import { useMemo } from 'react';
import { CardShell } from '@/components/cards/common';
import { VisibleFilterKey } from '@/components/cards/FilterCard/Filters';
import MouseFollowTooltip from '@/components/tooltips/MouseFollowTooltip';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import { useMouseFollowTooltip } from '@/hooks/useMouseFollowTooltip';
import type { BaseShow } from '@/types';
import { twMerge } from 'tailwind-merge';
import { buildShowsCountPrefix, useShowTitleList } from './ShowNamesLine';

type NetworkProfileCardProps = {
  className?: string;
  visibleFilters?: VisibleFilterKey[];
};

const NetworkShowsTooltipContent = ({ shows }: { shows: BaseShow[] }) => (
  <div>
    <h4 className="mb-2 font-medium text-sm">{buildShowsCountPrefix(shows.length)}</h4>
    <ul className="list-outside list-disc ml-6">
      {shows.map((show) => (
        <li className="mb-1 text-sm" key={show.id}>
          {(show.name ?? '').trim() || '—'}
        </li>
      ))}
    </ul>
  </div>
);

const NetworkProfileCard = ({ className, visibleFilters }: NetworkProfileCardProps) => {
  const { filters } = useFilterContext();
  const { selectedNetwork, shows } = useDerivedNetworkContext();
  // Use the shows aggregated across every selected network, not `selectedNetwork.shows` (the primary
  // network only) — otherwise the count, names line and tooltip under-report in multi-network mode.
  const networkShows = shows;
  const showCount = shows.length;
  const showNames = useMemo(
    () => shows.map((show) => show.name).filter(Boolean),
    [shows]
  );

  /** Show-level branding when the page exposes a show filter, or when the network has only one show. */
  const useShowLevel =
    visibleFilters === undefined ||
    visibleFilters.includes(VisibleFilterKey.Shows) ||
    showCount === 1;

  const selectedShowId = filters?.selectedShows[0];
  const explicitShow = selectedShowId ? shows.find((s) => s.id === selectedShowId) : undefined;
  const loneShow = showCount === 1 ? networkShows[0] : undefined;
  const selectedShow = useShowLevel ? explicitShow ?? loneShow : undefined;

  const selectedShowImageSrc =
    selectedShow && 'image_url' in selectedShow && selectedShow.image_url
      ? selectedShow.image_url
      : undefined;

  const { lineRef, countRef, description: showsDescription } = useShowTitleList(showCount, showNames);
  const showMultiShowListTooltip = showCount > 1;
  const { isOpen: isShowsTooltipOpen, point: showsTooltipPoint, containerProps: showsSubtitleContainerProps } =
    useMouseFollowTooltip(showMultiShowListTooltip);

  return (
    <CardShell type="secondary" className={twMerge('flex flex-col', className)}>
      <div className="flex w-full min-w-0 flex-row items-center overflow-hidden">
        {selectedShow ? (
          <div className="flex min-w-0 w-full flex-row items-center justify-start">
            {selectedShowImageSrc ? (
              <img
                data-testid="selected-show-image"
                className="mr-6 hidden h-[84px] w-[84px] shrink-0 rounded-lg object-cover sm:block"
                src={selectedShowImageSrc}
                alt=""
                width={84}
                height={84}
              />
            ) : null}

            <div className="flex w-full min-w-0 flex-col max-w-full gap-y-2">
              <div className="flex min-w-0 flex-row items-center">
                {selectedShowImageSrc ? (
                  <img
                    data-testid="selected-show-image-sm"
                    className="block h-8 w-8 mr-2 shrink-0 rounded-lg sm:hidden"
                    src={selectedShowImageSrc}
                    alt=""
                    width={32}
                    height={32}
                  />
                ) : null}
                <h2 className="min-w-0 flex-1 text-textDarkGrey text-2xl md:text-[34px] font-[700] font-telegraf overflow-hidden whitespace-nowrap text-ellipsis">
                  {selectedShow.name}
                </h2>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-row w-full min-w-0 items-center justify-start">
            <div className="flex w-full min-w-0 flex-col max-w-full gap-y-2">
              <div className="flex min-w-0 flex-row items-center">
                <h2 className="min-w-0 flex-1 text-textDarkGrey text-2xl md:text-[34px] font-[700] font-telegraf overflow-hidden whitespace-nowrap text-ellipsis">
                  {selectedNetwork.name ?? 'Network'}
                </h2>
              </div>
              {showCount > 0 && (
                <div
                  {...showsSubtitleContainerProps}
                  data-testid="network-shows-subtitle-row"
                  className="flex w-full min-w-0 max-w-full flex-row items-center gap-2"
                >
                  <p
                    ref={lineRef}
                    className="text-textMedGrey text-sm min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis"
                  >
                    <strong ref={countRef}>
                      {buildShowsCountPrefix(showCount)}
                    </strong>
                    {showsDescription ? `: ${showsDescription}` : ''}
                  </p>
                  {showMultiShowListTooltip ? (
                    <MouseFollowTooltip
                      isOpen={isShowsTooltipOpen}
                      clientX={showsTooltipPoint.x}
                      clientY={showsTooltipPoint.y}
                      zIndex={50}
                    >
                      <NetworkShowsTooltipContent shows={networkShows} />
                    </MouseFollowTooltip>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </CardShell>
  );
};

export default NetworkProfileCard;
